/* shellback, going away — the back of the same shell.
 *
 * SAME SHELL, PLATES THE OTHER WAY UP. The ink and the whole open/shut breath
 * are `shellback.ts`'s frame for frame; what is inverted is columns 3-12, the
 * interior.
 *
 * THE OUTLINE IS ITS OWN, and it did not used to be. Measured, every shellback
 * pair differed by EXACTLY 0.0px of silhouette across all eight frames -- one
 * blob with the interior repainted, three times -- against 46.1 for charger
 * and 39.9 for forager. Seen from behind the shell is NARROWER and TALLER than
 * in profile: a cell comes off each flank, the ink following so the dark
 * keyline stays on the edge where the whole roster wears it, and a capped
 * crown row sits above where the head is not. 21.8px a frame against the side,
 * 36.8 against the front. From behind you are looking at the underside of the plates the
 * side view shows you the top of, so pale and dark change places and the
 * creature reads as the same armour seen from the other end rather than as a
 * different animal. An earlier attempt drew a hard `o` ridge down the middle
 * instead and it split the shell into two objects on screen; a second one
 * darkened the top half and killed the cycle outright — steps fell to
 * 40/2/14/14/20/14/34/6, because the light-and-dark band swap IS the
 * animation and darkening the bands erases it.
 *
 * NO HEAD, AND THE FEET GO TO THE CORNERS. `shellback_down` puts a muzzle in
 * the rim row; there is nothing to put there from here. The two warm `e`
 * lights move to the outer corners instead — rear feet, planted wide — and
 * they spread and gather with the breath: columns 2-3/12-13 on the open
 * frames, 3-4/11-12 settling, 4-5/10-11 shut. Between them, two dark pixels
 * at columns 7-8: the tail, tucked under the rim.
 *
 * THE SHELL SETTLES, IT DOES NOT DEFLATE (2026-09-06). The header above used to claim ink
 * 128/126/124/122/116/122/126/128 and a swing of 9.5%. The art did not match it: measured off the grids
 * themselves, the shut frames were 112/118/114/90/84/90/118/112 -- a swing of 30.4% -- because they lost TWO ROWS
 * AND TWO COLUMNS, so the whole animal shrank and swelled instead of breathing. A shellback's shell is armour; it
 * is the one part that cannot compress. The shut frames keep the full footprint now: the crown tucks, the plate
 * bands swap, and the rear feet gather, which is what the cycle always said it was doing.
 *
 * Measured: ink 112/118/114/110/108/110/118/112 (median 112, swing 8.9%), steps 26/22/38/62/62/36/24/18 px,
 * spread 1.7x the median and 3.4x the smallest, floor row 14 in all eight frames.
 */

// phase 0 — open, arched, nine rows. Rear feet at the outer corners, tail
// tucked, and the shoulder band dark where the side view has it pale.
export const shellback_up_0 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '.......oo.......',
  '.....oooooo.....',
  '...oNNNNNNNNo...',
  '..onnnnnnnnnno..',
  '.oNNNNNNNNNNNNo.',
  '.oNNnnnnnnnnNNo.',
  '.oNNNNNNNNNNNNo.',
  '.oNnnnnnnnnnnNo.',
  '.oeennnNNnnneeo.',
  '..oooooooooooo..',
  '................',
];

// phase 1 — the shoulder is swallowed and the shell lands wide; the feet
// gather a column.
export const shellback_up_1 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '....oooooooo....',
  '..oooooooooooo..',
  '.oNnnnnnnnnnnNo.',
  '.oNNNNNNNNNNNNo.',
  '.oNNnnnnnnnnNNo.',
  '.oNnNNNNNNNNnNo.',
  '.oNnnnnnnnnnnNo.',
  '.oneennNNnneeno.',
  '.oooooooooooooo.',
  '................',
];

// phase 2 — the plates slide and the seam pinches. The feet hold.
export const shellback_up_2 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '.....oooooo.....',
  '...oooooooooo...',
  '.oNnnnnnnnnnnNo.',
  '.oNNnnnnnnnnNNo.',
  '.oNNNNNNNNNNNNo.',
  '.oNnnNNNNNNnnNo.',
  '.oNnnnnnnnnnnNo.',
  '.oneennNNnneeno.',
  '.oooooooooooooo.',
  '................',
];

// phase 3 — the shoulder pushes back out and the lowest band goes; the feet
// come in to their tightest stance as the armour shuts.
export const shellback_up_3 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '.....oooooo.....',
  '...oNNNNNNNNo...',
  '..onnnnnnnnnno..',
  '.oNNNNNNNNNNNNo.',
  '.oNnnnnnnnnnnNo.',
  '.oNNNNNNNNNNNNo.',
  '.oNNnnnnnnnnNNo.',
  '.oneennNNnneeno.',
  '..oooooooooooo..',
  '................',
];

// phase 4 — shut. One solid mass on its full base, feet in, tail down.
export const shellback_up_4 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '......oooo......',
  '...onnnnnnnno...',
  '..oNNNNNNNNNNo..',
  '.oNnnnnnnnnnnNo.',
  '.oNNNNNNNNNNNNo.',
  '.oNnnnnnnnnnnNo.',
  '.oNNNNNNNNNNNNo.',
  '.onneenNNneenno.',
  '..oooooooooooo..',
  '................',
];

// phase 5 — the crack. The interior opens before the outline does.
export const shellback_up_5 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '.....oooooo.....',
  '...oNNNNNNNNo...',
  '..onnnnnnnnnno..',
  '.oNNNNNNNNNNNNo.',
  '.oNNnnnnnnnnNNo.',
  '.oNNNNNNNNNNNNo.',
  '.oNnnnnnnnnnnNo.',
  '.oneennNNnneeno.',
  '..oooooooooooo..',
  '................',
];

// phase 6 — widest and flattest, the push-off frame; the feet start out again.
export const shellback_up_6 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '....oooooooo....',
  '..oooooooooooo..',
  '.oNnnnnnnnnnnNo.',
  '.oNNnnnnnnnnNNo.',
  '.oNNNNNNNNNNNNo.',
  '.oNnNNNNNNNNnNo.',
  '.oNnnnnnnnnnnNo.',
  '.oneennNNnneeno.',
  '.oooooooooooooo.',
  '................',
];

// phase 7 — up. Nine rows, feet back at the corners. Next: open.
export const shellback_up_7 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '.......oo.......',
  '.....oooooo.....',
  '...oNNNNNNNNo...',
  '..onnnnnnnnnno..',
  '.oNNnnnnnnnnNNo.',
  '.oNNNNNNNNNNNNo.',
  '.oNnNNNNNNNNnNo.',
  '.oNnnnnnnnnnnNo.',
  '.oeennnNNnnneeo.',
  '..oooooooooooo..',
  '................',
];
